import { useCallback, useRef, useState } from "react";
import { Upload, CheckCircle2, FileText, Sparkles, Camera, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import ReadabilityBadge, { ScanQualityTip } from "@/components/ReadabilityBadge";

export default function DocUpload({ caseId, requiredDocs, documents, onChange }) {
  const [selected, setSelected] = useState(requiredDocs?.[0]?.id || "petitioner_id");
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [applyBusy, setApplyBusy] = useState(false);
  const fileInput = useRef(null);

  const uploadedTypes = new Set(documents.map((d) => d.doc_type));

  const upload = useCallback(
    async (file) => {
      if (!file) return;
      setUploading(true);
      setLastResult(null);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/documents/upload`, fd, {
          params: { case_id: caseId, doc_type: selected },
          headers: { "Content-Type": "multipart/form-data" },
        });
        setLastResult(data);
        onChange?.();
      } catch (err) {
        console.error("Document upload failed:", err?.message || err);
        setLastResult({ error: err.response?.data?.detail || err.message });
      } finally {
        setUploading(false);
      }
    },
    [caseId, selected, onChange],
  );

  const applyLast = useCallback(async () => {
    if (!lastResult?.id) return;
    setApplyBusy(true);
    try {
      await api.post(`/documents/${lastResult.id}/apply`);
      onChange?.();
    } catch (err) {
      console.error("Apply extraction failed:", err?.message || err);
    } finally {
      setApplyBusy(false);
    }
  }, [lastResult?.id, onChange]);

  return (
    <div className="space-y-6" data-testid="doc-upload-panel">
      <RequiredDocsList
        requiredDocs={requiredDocs}
        selected={selected}
        setSelected={setSelected}
        uploadedTypes={uploadedTypes}
      />

      <ScanQualityTip />

      <DropZone fileInput={fileInput} uploading={uploading} onDrop={upload} />

      {uploading && <UploadingBanner />}

      {lastResult && !lastResult.error && (
        <ExtractResultCard result={lastResult} onApply={applyLast} applyBusy={applyBusy} />
      )}

      {lastResult?.error && (
        <div
          className="p-4 rounded-md text-sm"
          style={{ background: "#FEF2F2", color: "#991B1B" }}
        >
          {String(lastResult.error)}
        </div>
      )}

      {documents.length > 0 && <UploadedList documents={documents} />}
    </div>
  );
}

function RequiredDocsList({ requiredDocs, selected, setSelected, uploadedTypes }) {
  return (
    <div className="card-surface p-6">
      <div className="text-sm font-semibold mb-3" style={{ color: "var(--text-primary)" }}>
        Required documents
      </div>
      <div className="space-y-2">
        {requiredDocs.map((d) => {
          const done = uploadedTypes.has(d.id);
          const active = selected === d.id;
          return (
            <label
              key={d.id}
              className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors"
              data-testid={`doc-choice-${d.id}`}
              style={{
                background: active ? "var(--ai-bg)" : "#fff",
                border: `1px solid ${active ? "var(--ai-border)" : "var(--border-stone)"}`,
              }}
            >
              <input
                type="radio"
                name="doc-type"
                className="accent-orange-600"
                value={d.id}
                checked={active}
                onChange={() => setSelected(d.id)}
              />
              <div className="flex-1 text-sm">{d.label}</div>
              {done && (
                <span className="pill pill-ok">
                  <CheckCircle2 className="w-3 h-3" /> Uploaded
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function DropZone({ fileInput, uploading, onDrop }) {
  const cameraInput = useRef(null);
  return (
    <div
      className="dashed-drop p-6 sm:p-10 text-center cursor-pointer"
      data-testid="doc-drop-zone"
      onClick={() => fileInput.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) onDrop(f);
      }}
    >
      <input
        ref={fileInput}
        data-testid="doc-file-input"
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => onDrop(e.target.files?.[0])}
      />
      <input
        ref={cameraInput}
        data-testid="doc-camera-input"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => onDrop(e.target.files?.[0])}
      />
      <Upload className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--action)" }} />
      <div className="font-semibold text-sm sm:text-base" style={{ color: "var(--text-primary)" }}>
        {uploading ? "Extracting with AI…" : "Snap a photo or drop a PDF"}
      </div>
      <div className="text-[11px] sm:text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
        PDF · PNG · JPEG · WEBP up to 8 MB. We'll read, translate, and organize the information for you to review.
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
        <button
          type="button"
          data-testid="doc-camera-btn"
          className="btn-primary flex items-center justify-center gap-2 text-sm"
          onClick={(e) => { e.stopPropagation(); cameraInput.current?.click(); }}
        >
          <Camera className="w-4 h-4" /> Take a photo
        </button>
        <button
          type="button"
          data-testid="doc-choose-file-btn"
          className="btn-ghost flex items-center justify-center gap-2 text-sm"
          style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
          onClick={(e) => { e.stopPropagation(); fileInput.current?.click(); }}
        >
          <ImageIcon className="w-4 h-4" /> Choose from device
        </button>
      </div>
    </div>
  );
}

function UploadingBanner() {
  return (
    <div
      className="card-surface p-5 flex items-center gap-3 text-sm"
      style={{ color: "var(--ai-text)" }}
    >
      <div className="dot-typing">
        <span />
        <span />
        <span />
      </div>
      Reading document, translating and extracting fields…
    </div>
  );
}

function _applyButtonLabel(confirmed, busy) {
  if (confirmed) return "Applied";
  if (busy) return "Applying…";
  return "Confirm & auto-populate case";
}

function ExtractResultCard({ result, onApply, applyBusy }) {
  const extracted = result.extracted || {};
  const entries = Object.entries(extracted.extracted_fields || {}).filter(([, v]) => v);
  return (
    <div className="card-surface p-6 anim-in" data-testid="doc-extract-result">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
        <span className="font-semibold text-sm">Extracted from “{result.filename}”</span>
        <span className="pill pill-ai text-[10px]">{extracted.document_type}</span>
        <span className="pill pill-ai text-[10px]">Lang: {extracted.source_language}</span>
        <ReadabilityBadge readability={extracted.readability} compact />
      </div>
      <ReadabilityBadge readability={extracted.readability} />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        {entries.map(([k, v]) => (
          <div key={k} className="contents">
            <dt style={{ color: "var(--text-secondary)" }}>{k.replace(/_/g, " ")}</dt>
            <dd style={{ color: "var(--text-primary)" }}>{String(v)}</dd>
          </div>
        ))}
      </dl>
      {extracted.english_translation && (
        <details className="mt-3 text-xs" style={{ color: "var(--text-secondary)" }}>
          <summary className="cursor-pointer">Show OCR + English translation</summary>
          <div
            className="mt-2 whitespace-pre-wrap p-2 rounded"
            style={{ background: "var(--bg-app)" }}
          >
            <div className="font-semibold mb-1">OCR</div>
            {extracted.ocr_text}
            <div className="font-semibold mt-2 mb-1">English</div>
            {extracted.english_translation}
          </div>
        </details>
      )}
      <button
        data-testid="doc-apply-btn"
        className="btn-primary mt-4"
        onClick={onApply}
        disabled={applyBusy || result.confirmed}
      >
        {_applyButtonLabel(result.confirmed, applyBusy)}
      </button>
    </div>
  );
}

function UploadedList({ documents }) {
  return (
    <div className="card-surface p-6">
      <div className="text-sm font-semibold mb-3">Uploaded so far</div>
      <ul className="space-y-2 text-sm">
        {documents.map((d) => (
          <li key={d.id} className="flex items-center gap-2" data-testid={`uploaded-${d.id}`}>
            <FileText className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
            <span className="flex-1">{d.filename}</span>
            <span className="pill pill-ai text-[10px]">{d.doc_type}</span>
            {d.confirmed && <span className="pill pill-ok text-[10px]">Applied</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
